import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EmptyState from '@atlaskit/empty-state';
import Link from '@atlaskit/link';
import { getConfig } from '../../../../config/envConfig';
import { useToken, useSelectedWorkspace } from '../../../Flow/ConsentContextProvider';
import { useA2AContext, A2A_DOMAIN_STATUS } from '../A2AContextProvider';
import ErrorImage from '#assets/error.svg';
import LockImage from '#assets/restricted.svg';
import { a2aPolicyAlertMessages } from './messages';

const A2APolicyAlerts: FunctionComponent = () => {
  const token = useToken();
  const { formatMessage } = useIntl();
  const { selectedWorkspace } = useSelectedWorkspace();
  const { a2aDomainStatus } = useA2AContext();

  if (a2aDomainStatus !== A2A_DOMAIN_STATUS.blocked && a2aDomainStatus !== A2A_DOMAIN_STATUS.error && a2aDomainStatus !== A2A_DOMAIN_STATUS.a2aRestricted) return null;

  // A2A restricted by organization admin
  if (a2aDomainStatus === A2A_DOMAIN_STATUS.a2aRestricted) {
    const allowlistGuideUrl = getConfig().mcpPolicy.allowlistGuideUrl;
    return (
      <EmptyState
        header={formatMessage(a2aPolicyAlertMessages.a2aRestrictedTitle)}
        description={
          <FormattedMessage
            {...a2aPolicyAlertMessages.a2aRestrictedBody}
            values={{
              a2aRestrictedLink: (text) => (
                <Link href={allowlistGuideUrl} target="_blank" rel="noopener noreferrer">
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={LockImage}
      />
    );
  }

  const redirectUrl = token.redirectUrl?.callback ?? '';
  const siteLabel = selectedWorkspace?.trim() || formatMessage(a2aPolicyAlertMessages.siteFallback);

  // domain.allowed=false, domain.error=false → "Contact your admin"
  if (a2aDomainStatus === A2A_DOMAIN_STATUS.blocked) {
    const allowlistGuideUrl = getConfig().mcpPolicy.allowlistGuideUrl;
    return (
      <EmptyState
        header={formatMessage(a2aPolicyAlertMessages.blockedTitle)}
        description={
          <FormattedMessage
            {...a2aPolicyAlertMessages.blockedBody}
            values={{
              redirectUrl,
              siteLabel,
              allowlistLink: (text) => (
                <Link href={allowlistGuideUrl} target="_blank" rel="noopener noreferrer">
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={LockImage}
      />
    );
  }

  // domain.error=true → "Try again"
  const contactSupportUrl = getConfig().support.contactSupportUrl;
  return (
    <EmptyState
      header={formatMessage(a2aPolicyAlertMessages.errorTitle)}
      description={
        <FormattedMessage
          {...a2aPolicyAlertMessages.errorBody}
          values={{
            supportLink: (text) => (
              <Link href={contactSupportUrl} target="_blank" rel="noopener noreferrer">
                {text}
              </Link>
            ),
          }}
        />
      }
      imageUrl={ErrorImage}
    />
  );
};

export default A2APolicyAlerts;
